import { Button } from "@/components/ui/button.tsx"
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command.tsx"
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover.tsx"
import { classes } from "@/utils"
import { Check, ChevronsUpDown, Star } from "lucide-react"
import { useMemo, useRef, useState, useEffect } from "preact/hooks"

export interface MappingProductOption {
  id: string
  name: string
  sku?: string | null
  description?: string | null
}

export interface ProductMatchSuggestion {
  product_id: string
  score: number
  match_summary: string
}

interface ForecastProductMapSelectProps {
  products: MappingProductOption[]
  suggestions: ProductMatchSuggestion[]
  value: string
  onValueChange: (productId: string) => void
  className?: string
}

function product_label(p: MappingProductOption): string {
  return p.sku ? `${p.name} (${p.sku})` : p.name
}

function product_search_blob(p: MappingProductOption): string {
  return [p.name, p.sku ?? "", p.description ?? ""].join(" ").toLowerCase()
}

function get_search_tokens(q: string): string[] {
  return q
    .toLowerCase()
    .split(/\s+/)
    .map((t) => t.trim())
    .filter(Boolean)
}

function product_matches_search(p: MappingProductOption, tokens: string[]): boolean {
  if (tokens.length === 0) return true
  const blob = product_search_blob(p)
  return tokens.every((t) => blob.includes(t))
}

function score_color(score: number): string {
  if (score >= 0.8) return "text-green-600"
  if (score >= 0.55) return "text-yellow-700"
  return "text-gray-600"
}

export function ForecastProductMapSelect({
  products,
  suggestions,
  value,
  onValueChange,
  className,
}: ForecastProductMapSelectProps) {
  const [open, setOpen] = useState(false)
  const [searchValue, setSearchValue] = useState("")
  const [debouncedSearch, setDebouncedSearch] = useState("")
  const commandListRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(searchValue), 150)
    return () => clearTimeout(t)
  }, [searchValue])

  const suggestionById = useMemo(() => new Map(suggestions.map((s) => [s.product_id, s])), [suggestions])

  const orderedProducts = useMemo(() => {
    const sugIds = new Set(suggestions.map((s) => s.product_id))
    const suggested = suggestions
      .map((s) => products.find((p) => p.id === s.product_id))
      .filter((p): p is MappingProductOption => p != null)
    const rest = products.filter((p) => !sugIds.has(p.id)).sort((a, b) => a.name.localeCompare(b.name))
    return [...suggested, ...rest]
  }, [products, suggestions])

  const filtered = useMemo(() => {
    const tokens = get_search_tokens(debouncedSearch)
    return orderedProducts.filter((p) => product_matches_search(p, tokens))
  }, [orderedProducts, debouncedSearch])

  const selected = value ? products.find((p) => p.id === value) : undefined
  const selectedSug = value ? suggestionById.get(value) : undefined

  function stop_wheel_bubbling(e: WheelEvent) {
    e.stopPropagation()
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          className={classes(
            "min-w-[260px] justify-start text-left font-normal h-9 text-sm",
            className ?? ""
          )}>
          <div className="flex items-center gap-2 truncate w-full min-w-0">
            {selected ? (
              <>
                {selectedSug ? (
                  <Star className="h-3.5 w-3.5 text-yellow-500 shrink-0" />
                ) : (
                  <ChevronsUpDown className="h-4 w-4 shrink-0 text-gray-500" />
                )}
                <span className="truncate flex-1 min-w-0">{product_label(selected)}</span>
                {selectedSug ? (
                  <span className={classes("text-xs font-medium shrink-0", score_color(selectedSug.score))}>
                    {Math.round(selectedSug.score * 100)}%
                  </span>
                ) : null}
              </>
            ) : (
              <>
                <ChevronsUpDown className="h-4 w-4 shrink-0 text-gray-500" />
                <span className="text-gray-500">Search products…</span>
              </>
            )}
          </div>
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className="z-[100] w-[min(420px,92vw)] p-0 overflow-hidden"
        align="start"
        onWheel={stop_wheel_bubbling}>
        <Command shouldFilter={false} className="max-h-[min(400px,55vh)] flex flex-col">
          <CommandInput
            placeholder="Type to search…"
            value={searchValue}
            onValueChange={setSearchValue}
            className="h-9 text-sm"
          />
          <CommandList
            ref={commandListRef}
            className="max-h-[min(340px,50vh)] overflow-y-auto overscroll-y-contain"
            onWheel={stop_wheel_bubbling}>
            {debouncedSearch !== searchValue ? (
              <CommandEmpty className="text-sm py-6">Searching…</CommandEmpty>
            ) : filtered.length === 0 ? (
              <CommandEmpty className="text-sm py-6">No products match.</CommandEmpty>
            ) : (
              <CommandGroup>
                {filtered.map((p, index) => {
                  const sug = suggestionById.get(p.id)
                  const isPick = value === p.id
                  return (
                    <CommandItem
                      key={p.id}
                      value={`${product_search_blob(p)} ${p.id}`}
                      className={classes(
                        index % 2 === 0 ? "bg-slate-50/80" : "",
                        isPick ? "bg-blue-50" : ""
                      )}
                      onSelect={() => {
                        onValueChange(p.id)
                        setOpen(false)
                      }}>
                      <Check className={classes("mr-2 h-4 w-4 shrink-0", isPick ? "opacity-100" : "opacity-0")} />
                      <div className="flex flex-1 min-w-0 flex-col gap-0.5">
                        <div className="flex items-center gap-2 min-w-0">
                          <span className="font-medium truncate flex-1 min-w-0">{p.name}</span>
                          {sug ? (
                            <span className="flex items-center gap-0.5 shrink-0 text-xs">
                              <Star className="h-3 w-3 text-yellow-500" />
                              <span className={classes("font-medium", score_color(sug.score))}>
                                {Math.round(sug.score * 100)}%
                              </span>
                            </span>
                          ) : null}
                        </div>
                        <div className="text-xs text-gray-600 truncate">
                          {[p.sku ? `SKU ${p.sku}` : null, sug?.match_summary].filter(Boolean).join(" · ")}
                        </div>
                      </div>
                    </CommandItem>
                  )
                })}
              </CommandGroup>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  )
}

if (import.meta.vitest) {
  const { describe, expect, it } = import.meta.vitest

  describe("product_matches_search", () => {
    const p = { id: "1", name: "Sanzo Calamansi", sku: "SANZ-1", description: "12oz" }

    it("matches when all tokens appear in blob", () => {
      expect(product_matches_search(p, ["sanzo", "12"])).toBe(true)
    })

    it("fails when a token is missing", () => {
      expect(product_matches_search(p, ["sanzo", "grape"])).toBe(false)
    })
  })
}
